import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    ShowBase,
    useNotify,
    useShowContext,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import {
    showMainSx,
    showPageRowSx,
} from '@/components/layout/showPageLayout';
import { ContractorOwnerAside } from './ContractorOwnerAside';
import { ContractorOwner } from '@/types';
import {
    ContractorNetwork,
    fetchContractorNetwork,
} from '@/utils/contractor_owner_utils';

export const ContractorOwnerShow = () => (
    <ShowBase>
        <ContractorOwnerShowContent />
    </ShowBase>
);

const ContractorOwnerShowContent = () => {
    const { record, isPending, error } = useShowContext<ContractorOwner>();
    if (isPending) return null;
    if (error || !record) {
        return (
            <ResourceNotFound
                resourceLabel="Contractor"
                listPath="/contractor_owners"
                listLabel="Back to contractors"
            />
        );
    }

    return (
        <Box sx={showPageRowSx}>
            <ContractorOwnerAside />
            <Box sx={showMainSx}>
                <Card>
                    <CardContent>
                        <Box sx={{ mb: 2, ml: { xs: 0, sm: 2 }, minWidth: 0 }}>
                            <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
                                {record.name}
                            </Typography>
                            <Typography
                                variant="body1"
                                component="div"
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {record.company_name}
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {record.email}
                            </Typography>
                            <Typography variant="body2" component="div">
                                {record.phone_number}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
                <ContractorNetworkLists contractorId={Number(record.id)} />
            </Box>
        </Box>
    );
};

const ContractorNetworkLists = ({ contractorId }: { contractorId: number }) => {
    const notify = useNotify();
    const [network, setNetwork] = useState<ContractorNetwork | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchContractorNetwork(contractorId)
            .then(data => {
                if (!cancelled) setNetwork(data);
            })
            .catch(err => {
                if (!cancelled) {
                    notify(
                        err instanceof Error
                            ? err.message
                            : 'Failed to load contractor network',
                        { type: 'error' },
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, [contractorId, notify]);

    return (
        <Box
            mt={2}
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={2}
            sx={{ minWidth: 0 }}
        >
            <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Properties
                    </Typography>
                    {!network?.properties.length ? (
                        <Typography variant="body2" color="text.secondary">
                            No properties linked as contractor
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {network.properties.map(property => (
                                <ListItemButton
                                    key={property.project_id}
                                    component={Link}
                                    to={`/projects/${property.project_id}/show`}
                                >
                                    <ListItemText
                                        primary={property.address || 'Untitled address'}
                                        secondary={
                                            property.property_owner_name
                                                ? `Owner: ${property.property_owner_name}`
                                                : undefined
                                        }
                                        primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                                        secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Owners
                    </Typography>
                    {!network?.owners.length ? (
                        <Typography variant="body2" color="text.secondary">
                            No owners linked through projects
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {network.owners.map(owner => (
                                <ListItemButton
                                    key={owner.id}
                                    component={Link}
                                    to={`/contractor_owners/${owner.id}/show`}
                                >
                                    <ListItemText
                                        primary={owner.name}
                                        primaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
